// Politica de Privacidade (LGPD) — modal compartilhado entre login aluno/professora
import React from 'react';
import { TOK } from '../design/tokens';
import { GeoLogo } from '../design/GeoLogo';

export function PrivacyPolicy({ onClose }: { onClose: () => void }) {
  const H: React.CSSProperties = { fontWeight: 800, fontSize: 15, color: TOK.navy, margin: '18px 0 6px', fontFamily: TOK.display };
  const P: React.CSSProperties = { fontSize: 13, color: TOK.ink70, lineHeight: 1.6, margin: '6px 0' };
  const LI: React.CSSProperties = { fontSize: 13, color: TOK.ink70, lineHeight: 1.6, margin: '2px 0' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,17,40,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: TOK.sans }}>
      <div style={{ background: 'white', borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(10,17,40,.25)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${TOK.border}`, background: TOK.tealSoft }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GeoLogo size={28} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: TOK.navy, fontFamily: TOK.display }}>Política de Privacidade</div>
              <div style={{ fontSize: 11, color: TOK.ink50 }}>GEO Frequência — LGPD</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TOK.ink50, fontSize: 22, padding: 0, lineHeight: 1 }} aria-label="Fechar" title="Fechar">×</button>
        </div>

        {/* Content */}
        <div style={{ padding: '8px 24px 20px', overflowY: 'auto' }}>
          <div style={{ fontSize: 11, color: TOK.ink50, marginTop: 10 }}>Última atualização: 24/04/2026</div>

          <div style={H}>1. Quem somos</div>
          <p style={P}>O <strong>GEO Frequência</strong> é um sistema desenvolvido pela <strong>Profª Laura Valencia</strong> (UFRJ) para controle de frequência na disciplina de Gestão Estratégica de Operações (GEO). Este documento descreve como os seus dados pessoais são coletados, utilizados e protegidos, em conformidade com a Lei Geral de Proteção de Dados (<strong>LGPD — Lei 13.709/2018</strong>).</p>

          <div style={H}>2. Dados que coletamos</div>
          <ul style={{ paddingLeft: 22, margin: '6px 0' }}>
            <li style={LI}><strong>Nome completo</strong> — exibição na lista de chamada e relatórios</li>
            <li style={LI}><strong>DRE</strong> — matrícula da UFRJ (identificação única)</li>
            <li style={LI}><strong>E-mail</strong> — autenticação e comunicação acadêmica</li>
            <li style={LI}><strong>Turma</strong> — organização da chamada</li>
            <li style={LI}><strong>Senha</strong> — armazenada como hash irreversível (PBKDF2-HMAC-SHA256)</li>
            <li style={LI}><strong>Registros de presença</strong> — data, hora e sessão de cada presença</li>
          </ul>

          <div style={H}>3. Finalidade do tratamento</div>
          <p style={P}>Os dados são utilizados <strong>exclusivamente</strong> para:</p>
          <ul style={{ paddingLeft: 22, margin: '6px 0' }}>
            <li style={LI}>Controle de frequência acadêmica (obrigação legal — normas da UFRJ)</li>
            <li style={LI}>Geração de relatórios para a Secretaria Acadêmica</li>
            <li style={LI}>Comunicação com o(a) aluno(a) sobre seu status de frequência</li>
          </ul>

          <div style={H}>4. Seus direitos (Art. 18 da LGPD)</div>
          <ul style={{ paddingLeft: 22, margin: '6px 0' }}>
            <li style={LI}><strong>Acesso e portabilidade</strong> — você pode exportar seus dados a qualquer momento pelo botão <em>"Exportar meus dados"</em> no painel do aluno</li>
            <li style={LI}><strong>Correção</strong> — solicite à professora a correção de dados incompletos ou desatualizados</li>
            <li style={LI}><strong>Eliminação</strong> — você pode apagar seu cadastro a qualquer momento pelo botão <em>"Apagar meu cadastro"</em> no painel do aluno</li>
            <li style={LI}><strong>Informação</strong> — saber com quem seus dados foram compartilhados</li>
            <li style={LI}><strong>Revogação do consentimento</strong> — você pode revogar o consentimento a qualquer momento</li>
          </ul>

          <div style={H}>5. Compartilhamento</div>
          <p style={P}>Seus dados são compartilhados apenas com:</p>
          <ul style={{ paddingLeft: 22, margin: '6px 0' }}>
            <li style={LI}><strong>Secretaria Acadêmica da UFRJ</strong> — via relatórios de frequência (obrigação legal)</li>
            <li style={LI}><strong>Google Firebase (Firestore)</strong> — armazenamento seguro e criptografado</li>
          </ul>
          <p style={P}>Seus dados <strong>nunca</strong> são vendidos, compartilhados com anunciantes ou usados para fins comerciais.</p>

          <div style={H}>6. Segurança</div>
          <ul style={{ paddingLeft: 22, margin: '6px 0' }}>
            <li style={LI}>Senhas armazenadas com <strong>hash PBKDF2</strong> (100.000 iterações + salt único)</li>
            <li style={LI}>Conexões criptografadas (<strong>HTTPS/TLS</strong>)</li>
            <li style={LI}>Proteção contra bots via <strong>reCAPTCHA v3</strong> e <strong>Firebase App Check</strong></li>
            <li style={LI}>Autenticação em duas etapas para a docente</li>
            <li style={LI}>Cabeçalhos de segurança (CSP, HSTS, X-Frame-Options)</li>
          </ul>

          <div style={H}>7. Retenção de dados</div>
          <p style={P}>Os dados são mantidos enquanto o período letivo estiver ativo e pelo prazo exigido pela UFRJ para fins acadêmicos. Após esse prazo, podem ser eliminados automaticamente ou mediante solicitação via botão <em>"Apagar meu cadastro"</em>.</p>

          <div style={H}>8. Cookies e armazenamento local</div>
          <p style={P}>Utilizamos <strong>localStorage</strong> do navegador apenas para manter sua sessão ativa e permitir uso offline. Nenhum dado é enviado a terceiros para rastreamento.</p>

          <div style={H}>9. Contato</div>
          <p style={P}>Dúvidas sobre esta política ou sobre seus dados pessoais:<br /><strong>Profª Laura Valencia</strong> — <a href="mailto:geofrequencia@gmail.com" style={{ color: TOK.teal }}>geofrequencia@gmail.com</a></p>

          <div style={{ marginTop: 18, padding: 12, background: TOK.tealSoft, borderRadius: 10, fontSize: 12, color: TOK.navy, lineHeight: 1.5 }}>
            Ao utilizar o GEO Frequência, você concorda com esta Política de Privacidade. Você pode revogar seu consentimento a qualquer momento apagando seu cadastro.
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${TOK.border}`, background: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: TOK.teal, color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: TOK.display }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
